import { useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';

const FORMAT_COLS = {
  Skit: [
    { key: 'character', label: 'Character' },
    { key: 'line',      label: 'Line' },
    { key: 'notes',     label: 'Directional Notes' },
    { key: 'scene',     label: 'Scene Description' },
    { key: 'props',     label: 'Props' },
  ],
  TH: [
    { key: 'narration', label: 'Narration' },
    { key: 'aRoll',     label: 'A Roll' },
    { key: 'bRoll',     label: 'B Roll' },
    { key: 'vfx',       label: 'VFX' },
    { key: 'sfx',       label: 'SFX' },
    { key: 'props',     label: 'Props' },
  ],
  AI: [
    { key: 'narration', label: 'Narration' },
    { key: 'visuals',   label: 'Visuals' },
  ],
  Longform: [
    { key: 'sceneNumber', label: 'Scene #' },
    { key: 'character',   label: 'Character' },
    { key: 'line',        label: 'Line' },
    { key: 'scene',       label: 'Scene Description' },
  ],
  Mockumentary: [
    { key: 'character', label: 'Character' },
    { key: 'line',      label: 'Line' },
    { key: 'notes',     label: 'Directional Notes' },
    { key: 'aroll',     label: 'A-Roll' },
    { key: 'broll',     label: 'B-Roll' },
    { key: 'vfx',       label: 'FX' },
    { key: 'props',     label: 'Props' },
    { key: 'location',  label: 'Location' },
  ],
};

export default function ScriptShareView() {
  const { token } = useParams();
  const [guestName,     setGuestName]     = useState('');
  const [guestEmail,    setGuestEmail]    = useState('');
  const [gateSubmitted, setGateSubmitted] = useState(false);
  const [script,        setScript]        = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');

  async function handleGateSubmit(e) {
    e.preventDefault();
    if (!guestName.trim() || !guestEmail.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/share-links/scripts/${token}`);
      setScript(res.data);
      setGateSubmitted(true);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('This link is no longer active or does not exist.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  const format = script?.scriptBody?.format;
  const scenes = script?.scriptBody?.scenes || [];
  const cols   = FORMAT_COLS[format] || [];

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg-app)', color: 'var(--tx-primary)',
      fontFamily: 'var(--font)', display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 32px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'var(--bg-base)', flexShrink: 0,
      }}>
        <div style={{
          width: 28, height: 28, background: 'var(--violet-soft)',
          border: '1px solid var(--violet-glow)', borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 12, color: 'var(--violet)', flexShrink: 0,
        }}>HF</div>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx-primary)' }}>Helicon Flow</span>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 24px', overflowY: 'auto' }}>

        {!gateSubmitted ? (
          // ── Guest gate ──
          <div style={{
            background: 'var(--bg-card)', borderRadius: 'var(--radius-card)',
            border: '1px solid var(--border)', padding: '36px 40px',
            width: '100%', maxWidth: 420, boxShadow: 'var(--shadow-lg)',
          }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--tx-primary)', letterSpacing: '-0.02em', marginBottom: 8 }}>
              View Script
            </div>
            <div style={{ fontSize: 13, color: 'var(--tx-muted)', marginBottom: 28 }}>
              Enter your name and email to view this shared script.
            </div>

            {error ? (
              <div style={{
                padding: '14px 16px', borderRadius: 'var(--radius-md)',
                background: 'rgba(212,85,85,0.1)', border: '1px solid rgba(212,85,85,0.3)',
                color: 'var(--s-incomplete-t)', fontSize: 13,
              }}>
                {error}
              </div>
            ) : (
              <form onSubmit={handleGateSubmit}>
                <div className="form-field">
                  <label className="form-label">Name</label>
                  <input
                    className="form-input"
                    value={guestName}
                    onChange={e => setGuestName(e.target.value)}
                    placeholder="Your name"
                    required
                    autoFocus
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Email</label>
                  <input
                    className="form-input"
                    type="email"
                    value={guestEmail}
                    onChange={e => setGuestEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading || !guestName.trim() || !guestEmail.trim()}
                  style={{ width: '100%', marginTop: 8, padding: '11px', fontSize: 14 }}
                >
                  {loading ? 'Loading…' : 'View Script →'}
                </button>
              </form>
            )}
          </div>
        ) : (
          // ── Script view ──
          <div style={{ width: '100%', maxWidth: 1100 }}>
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--tx-primary)', letterSpacing: '-0.02em', lineHeight: 1.1, marginBottom: 8 }}>
                {script.scriptName}
              </div>
              <div style={{ fontSize: 13, color: 'var(--tx-muted)', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                {script.clientName && <span>{script.clientName}</span>}
                {script.clientName && <span style={{ color: 'var(--tx-faint)' }}>·</span>}
                {script.writerName && <span>Written by {script.writerName}</span>}
                {format && (
                  <>
                    <span style={{ color: 'var(--tx-faint)' }}>·</span>
                    <span style={{ color: 'var(--violet)', fontWeight: 600, fontSize: 11, letterSpacing: '0.02em' }}>
                      {format === 'TH' ? 'Talking Head' : format}
                    </span>
                  </>
                )}
              </div>
            </div>

            {scenes.length > 0 && cols.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%', borderCollapse: 'collapse',
                  background: 'var(--bg-card)', borderRadius: 'var(--radius-md)',
                  overflow: 'hidden', border: '1px solid var(--border)',
                }}>
                  <thead>
                    <tr>
                      {cols.map(col => (
                        <th key={col.key} style={{
                          padding: '10px 14px', fontSize: 11, fontWeight: 600,
                          color: 'var(--tx-muted)', textTransform: 'uppercase',
                          letterSpacing: '0.05em', borderBottom: '1px solid var(--border)',
                          textAlign: 'left', background: 'var(--bg-card2)',
                          whiteSpace: 'nowrap',
                        }}>
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {scenes.map((scene, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                        {cols.map(col => (
                          <td key={col.key} style={{
                            padding: '10px 14px', fontSize: 13,
                            color: 'var(--tx-primary)', verticalAlign: 'top',
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          }}>
                            {scene[col.key] || ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ color: 'var(--tx-faint)', fontSize: 13, padding: '20px 0' }}>
                No script content available.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '14px 32px', borderTop: '1px solid var(--border)',
        textAlign: 'center', fontSize: 11, color: 'var(--tx-faint)', flexShrink: 0,
      }}>
        Powered by Heliconflow
      </div>
    </div>
  );
}
